import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Image } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PROJECT_TYPES = [
  { value: "rooftop", label: "Rooftop" },
  { value: "ground_mount", label: "Ground Mount" },
  { value: "ground_mount_oa", label: "Ground Mount – Open Access" },
  { value: "blank", label: "Custom (no template)" },
];

const inputCls = "w-full border border-[rgba(46,100,68,0.15)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#2F7D52] bg-white text-[#1B2B22]";
const labelCls = "block text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5";

/**
 * Shared "Edit Project Details" dialog — used by the project list AND the
 * project dashboard so the two never drift again.
 *
 * props: project (object|null), open, onOpenChange(bool), onSaved(updatedProject), teamMembers? ([{id, full_name}])
 */
export default function EditProjectDialog({ project, open, onOpenChange, onSaved, teamMembers = [] }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  useEffect(() => {
    if (open && project) {
      setForm({
        ...project,
        expected_cod: (project.expected_cod || "").split("T")[0],
        team_member_ids: project.team_member_ids || [],
      });
    }
  }, [open, project]);

  const GST_STATES = ["Andaman and Nicobar Islands","Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chandigarh","Chhattisgarh","Dadra and Nagar Haveli and Daman and Diu","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jammu and Kashmir","Jharkhand","Karnataka","Kerala","Ladakh","Lakshadweep","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Puducherry","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal"];
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        client_name: form.client_name,
        client_email: form.client_email || null,
        client_phone: form.client_phone || null,
        location: form.location,
        site_state: form.site_state || null,
        capacity_kwp: parseFloat(form.capacity_kwp) || 0,
        contract_value: parseFloat(form.contract_value) || 0,
        expected_cod: form.expected_cod || null,
        description: form.description || null,
        project_type: form.project_type || null,
        team_member_ids: form.team_member_ids,
      };
      // null values are dropped server-side (fields can't be cleared yet) — send only what has a value
      Object.keys(payload).forEach((k) => payload[k] === null && delete payload[k]);
      const { data } = await axios.put(`${API}/projects/${project.id}`, payload);
      const sched = data.schedule;
      if (sched && sched.scheduled > 0) {
        toast.success(`Project updated — ${sched.scheduled} task${sched.scheduled === 1 ? "" : "s"} scheduled up to ${new Date(sched.end).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`);
      } else {
        toast.success("Project updated");
      }
      onSaved && onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update project");
    }
    setSaving(false);
  };

  const uploadClientLogo = async (file) => {
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/projects/${project.id}/upload-client-logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("client_logo_url", data.client_logo_url);
      onSaved && onSaved({ ...project, client_logo_url: data.client_logo_url });
      toast.success("Client logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload logo");
    }
    setLogoUploading(false);
  };

  const toggleMember = (id) => set("team_member_ids", form.team_member_ids.includes(id) ? form.team_member_ids.filter((x) => x !== id) : [...form.team_member_ids, id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-project-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-[#1B2B22]">Edit Project Details</DialogTitle>
        </DialogHeader>
        {form && (
          <form onSubmit={save} className="space-y-4 py-2">
            <div>
              <label className={labelCls}>Project Name *</label>
              <input type="text" required value={form.name || ""} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="edit-project-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Client Name *</label>
                <input type="text" required value={form.client_name || ""} onChange={(e) => set("client_name", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Client Email</label>
                <input type="email" value={form.client_email || ""} onChange={(e) => set("client_email", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Client Phone</label>
                <input type="text" value={form.client_phone || ""} onChange={(e) => set("client_phone", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Location</label>
                <input type="text" value={form.location || ""} onChange={(e) => set("location", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Site state <span className="font-normal text-[#94A3B8]">(place of supply for invoices)</span></label>
                <select value={form.site_state || ""} onChange={(e) => set("site_state", e.target.value)} className={inputCls} data-testid="edit-site-state">
                  <option value="">Derive from location</option>
                  {GST_STATES.map((st) => <option key={st} value={st}>{st}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Capacity (kWp)</label>
                <input type="number" step="any" value={form.capacity_kwp ?? ""} onChange={(e) => set("capacity_kwp", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Contract Value (₹)</label>
                <input type="number" step="any" value={form.contract_value ?? ""} onChange={(e) => set("contract_value", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Expected COD</label>
                <input type="date" value={form.expected_cod || ""} onChange={(e) => set("expected_cod", e.target.value)} className={inputCls} data-testid="edit-project-cod" />
                <p className="text-[10px] text-[#8AA496] mt-1">Setting or changing the COD re-plans the open tasks automatically.</p>
              </div>
              <div>
                <label className={labelCls}>Project Type</label>
                <select value={form.project_type || "rooftop"} onChange={(e) => set("project_type", e.target.value)} className={inputCls}>
                  {PROJECT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>Description</label>
              <textarea value={form.description || ""} onChange={(e) => set("description", e.target.value)} rows={2} className={inputCls + " resize-none"} />
            </div>

            {teamMembers.length > 0 && (
              <div>
                <label className={labelCls}>Team</label>
                <div className="flex flex-wrap gap-1.5">
                  {teamMembers.map((m) => {
                    const on = form.team_member_ids.includes(m.id);
                    return (
                      <button type="button" key={m.id} onClick={() => toggleMember(m.id)}
                        className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-[#2F7D52] text-white border-[#2F7D52]" : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#2F7D52]"}`}>
                        {m.full_name || m.name || m.email}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <label className={labelCls}>Client Logo</label>
              <div className="flex items-center gap-3">
                {form.client_logo_url && (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${form.client_logo_url}`} alt="client logo" className="h-10 w-auto object-contain border border-[rgba(46,100,68,0.1)] rounded p-1 bg-white" />
                )}
                <label className={`flex items-center gap-2 cursor-pointer border border-dashed border-[#E2E8F0] rounded px-3 py-2 text-xs text-[#64748B] hover:border-[rgba(47,125,82,0.5)] hover:text-[#2F7D52] transition-colors ${logoUploading ? "opacity-50 pointer-events-none" : ""}`}>
                  {logoUploading ? <><Upload size={12} className="animate-pulse" />Uploading...</> : <><Image size={12} />{form.client_logo_url ? "Change Logo" : "Upload Client Logo"}</>}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadClientLogo(e.target.files[0])} data-testid="client-logo-upload" />
                </label>
                <span className="text-[10px] text-[#94A3B8]">JPG, PNG, SVG</span>
              </div>
            </div>

            <DialogFooter>
              <button type="button" onClick={() => onOpenChange(false)} className="px-4 py-2 border border-[rgba(46,100,68,0.15)] rounded-lg text-sm text-[#8AA496] hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={saving} className="px-4 py-2 bg-[#2F7D52] text-white rounded-lg text-sm font-semibold hover:bg-[#25653F] disabled:opacity-60" data-testid="edit-project-save">
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
